import React, { useCallback, useEffect, useMemo, useState } from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import Pagination from "../../shared/Pagination";
import { getCiReportSiteComparison } from "../../../api/competitiveIntelligence";
import { InferredOccupancy } from "../../../utils/ciFormat";
import ThinScroll from "../../shared/ThinScroll";
import { PanelTakeaway, fmtDateShort, fmtRate } from "../shared";


const PAGE_SIZE = 15; // 15/page — the house standard across all CI report tables
// Fetch all rows in one call so the site-type filter (applied client-side)
// can search the full dataset, not just the rows on the current page.
// Pagination then runs client-side over the filtered result.
const FETCH_LIMIT = 1000;

const POSITION_STYLES = {
  UNDERPRICED: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  COMPETITIVE: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  PREMIUM: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
};

// Map server availability values to a small visual cue. `unknown` / null / missing
// statuses render as a plain em dash per spec — no dot, no "Unknown" label.
const AVAIL_STYLES = {
  available:    { dot: "bg-emerald-500", label: "Available" },
  minimum_stay: { dot: "bg-amber-500",   label: "Min stay" },
  sold_out:     { dot: "bg-amber-500",   label: "Limited Avail." },
};

const PositionBadge = ({ position }) => {
  const cls = POSITION_STYLES[position] || "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] uppercase tracking-wide font-semibold ${cls}`}>
      {position || "—"}
    </span>
  );
};

const AvailDot = ({ status }) => {
  const meta = AVAIL_STYLES[status];
  if (!meta) return <span className="text-xs text-gray-500 dark:text-gray-400">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5" title={meta.label}>
      <span className={`inline-block w-2 h-2 rounded-full ${meta.dot}`} />
      <span className="text-xs text-gray-600 dark:text-gray-300">{meta.label}</span>
    </span>
  );
};

const SiteComparisonBody = ({ rows, normalizedGroup }) => {
  const [page, setPage] = useState(1);

  // Filter client-side by the page-level Site Type filter, matching against
  // the raw `your_site_type` value. The page sources the dropdown from the
  // same raw vocabulary, so values match by string equality. Backend's
  // `normalized_group` column has data-quality issues we deliberately skip.
  const filtered = useMemo(() => {
    const base = normalizedGroup
      ? rows.filter((row) => row.your_site_type === normalizedGroup)
      : rows;
    return [...base].sort((rowA, rowB) => (rowA.check_in || "").localeCompare(rowB.check_in || ""));
  }, [rows, normalizedGroup]);

  // Reset to page 1 whenever the filter changes — otherwise we could be sitting
  // on (say) page 5 of an unfiltered set when the filter knocks the result down
  // to 3 rows total.
  useEffect(() => { setPage(1); }, [normalizedGroup]);

  // Pagination runs client-side over the filtered result so the page count
  // and "Showing N–M of T" line stay honest as the filter narrows things.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  if (!rows.length) {
    return <PanelEmpty message="No site comparison data for the selected category." />;
  }

  return (
    <div>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center gap-3 flex-wrap">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {filtered.length} {filtered.length === 1 ? "row" : "rows"}
          {normalizedGroup ? ` · ${normalizedGroup}` : ""}
        </span>
      </div>

      <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
        <table className="w-full min-w-[1200px] text-sm table-fixed responsive-card-table">
          <colgroup>
            <col style={{ width: "8%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "11%" }} />
          </colgroup>
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-4 py-2.5 text-left font-semibold first:pl-5">Date</th>
              <th className="px-4 py-2.5 text-left font-semibold">Site Type</th>
              <th className="px-4 py-2.5 text-left font-semibold">Avail</th>
              <th className="px-4 py-2.5 text-right font-semibold">Your Rate</th>
              <th className="px-4 py-2.5 text-right font-semibold">Market Avg</th>
              <th className="px-4 py-2.5 text-right font-semibold">Market Min</th>
              <th className="px-4 py-2.5 text-right font-semibold">Market Max</th>
              <th className="px-4 py-2.5 text-right font-semibold">Suggested</th>
              <th className="px-4 py-2.5 text-left font-semibold">Position</th>
              <th className="px-4 py-2.5 text-right font-semibold last:pr-5">Comp / Your Occupancy</th>
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((row, index) => (
              <tr key={`${row.check_in}-${row.your_site_type}-${row.date_window_name || index}`} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <td data-label="Date" className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap first:pl-5">{fmtDateShort(row.check_in)}</td>
                <td data-label="Site Type" className="px-4 py-2.5 text-gray-700 dark:text-gray-300">
                  <div className="truncate" title={row.your_site_type || ""}>
                    {row.your_site_type || "—"}
                  </div>
                </td>
                <td data-label="Avail" className="px-4 py-2.5 whitespace-nowrap"><AvailDot status={row.your_availability} /></td>
                <td data-label="Your" className="px-4 py-2.5 text-right font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtRate(row.your_price)}</td>
                <td data-label="Market Avg" className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtRate(row.market_avg)}</td>
                <td data-label="Market Min" className="px-4 py-2.5 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtRate(row.market_min)}</td>
                <td data-label="Market Max" className="px-4 py-2.5 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtRate(row.market_max)}</td>
                <td data-label="Suggested" className="px-4 py-2.5 text-right text-emerald-600 dark:text-emerald-400 whitespace-nowrap">{fmtRate(row.suggested_price)}</td>
                <td data-label="Position" className="px-4 py-2.5 whitespace-nowrap"><PositionBadge position={row.price_position} /></td>
                <td data-label="Comp / Your Occupancy" className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap last:pr-5">
                  <span>
                    <InferredOccupancy value={row.comp_occupancy} /> / <InferredOccupancy value={row.your_occupancy} />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ThinScroll>

      {totalPages > 1 && (
        <div className="px-5 pt-1 pb-3 border-t border-gray-200 dark:border-gray-800">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={filtered.length}
            perPage={PAGE_SIZE}
            label="rows"
          />
        </div>
      )}
    </div>
  );
};

const SiteComparisonPanel = ({ clientId, categoryCode, normalizedGroup, say }) => {
  // Fetch the full set in one call (limit=1000). The page-level Site Type
  // filter is applied client-side over the result so the user sees all rows
  // and can clear the filter without a refetch.
  const fetcher = useCallback(
    () => getCiReportSiteComparison(clientId, { categoryCode: categoryCode || "rv", page: 1, limit: FETCH_LIMIT }),
    [clientId, categoryCode]
  );

  return (
    <AnalysisPanel
      id={`site-comparison-${clientId}-${categoryCode || "all"}`}
      title="Site Comparison"
      tip="Per site-type comparison of your rate vs the market on the same date. Suggested price column gives a target based on where similar sites are priced in the market."
      description="Per-site-type pricing vs market with suggested rate. Use the page Site type filter to drill in."
      badge={categoryCode ? categoryCode.toUpperCase() : null}
      fetcher={fetcher}
      deps={[clientId, categoryCode]}
    >
      {({ data }) => {
        const items = Array.isArray(data) ? data : (data?.items ?? []);
        return (
          <>
            <PanelTakeaway panelKey="site_comparison" say={say} />
            <SiteComparisonBody rows={items} normalizedGroup={normalizedGroup} />
          </>
        );
      }}
    </AnalysisPanel>
  );
};

export default SiteComparisonPanel;
